import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { BarChart3, Clock, ClipboardCheck, AlertTriangle, Sparkles } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const iso = (d) => d.toISOString().slice(0, 10);

export default function FieldOpsKPIs() {
  const today = new Date();
  const ago = new Date(); ago.setDate(ago.getDate() - 30);
  const [from, setFrom] = useState(iso(ago));
  const [to, setTo] = useState(iso(today));
  const { data } = useQuery({
    queryKey: ["fo-kpis", from, to],
    queryFn: async () => (await api.get(`/fieldops/kpis?date_from=${from}&date_to=${to}`)).data,
  });
  const k = data || {};

  const Card = ({ icon: Icon, label, value, sub, testid }) => (
    <div data-testid={testid} className="rounded-2xl bg-white border border-[#E3DED4] p-5">
      <div className="flex items-center gap-2 text-[#8A8577] text-xs uppercase tracking-wider"><Icon className="h-4 w-4" /> {label}</div>
      <div className="font-serif text-3xl text-[#45423C] mt-2">{value}</div>
      {sub && <div className="text-xs text-[#6F6A60] mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="fo-kpis-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Εργασίες Πεδίου</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><BarChart3 className="h-7 w-7" /> KPIs & Αποτελέσματα</h1>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="kpi-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
          <span className="text-[#8A8577]">–</span>
          <input data-testid="kpi-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card testid="kpi-visits" icon={ClipboardCheck} label="Εργασίες" value={k.visits_total ?? "—"} sub="υποβληθείσες" />
        <Card testid="kpi-avg" icon={Clock} label="Μέσος χρόνος" value={`${k.avg_duration_min ?? 0}′`} sub="ανά εργασία" />
        <Card testid="kpi-completion" icon={Sparkles} label="Ολοκλήρωση" value={`${k.completion_pct ?? 0}%`} sub={`${k.linens_refreshed ?? 0} αλλαγές λευκών`} />
        <Card testid="kpi-issues" icon={AlertTriangle} label="Ανοιχτές βλάβες" value={k.issues_open ?? 0} sub={`από ${k.issues_total ?? 0} σύνολο`} />
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5">
        <h2 className="font-serif text-xl text-[#45423C] mb-4">Εργασίες ανά ημέρα</h2>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={k.by_day || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EDE8DF" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8A8577" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fontSize: 11, fill: "#8A8577" }} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="count" fill="#6E7A63" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Table title="Ανά κατηγορία" rows={(k.by_category || []).map((c) => [c.label, c.count, `${c.avg_min}′`])} testid="kpi-by-category" />
        <Table title="Ανά εργαζόμενο" rows={(k.by_worker || []).map((w) => [w.worker, w.count, `${w.avg_min}′`])} testid="kpi-by-worker" />
      </div>
      <Table title="Ανά μονάδα" rows={(k.by_unit || []).map((u) => [u.unit, u.count, `${u.avg_min}′`])} testid="kpi-by-unit" />
    </div>
  );
}

function Table({ title, rows, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={testid}>
      <h2 className="font-serif text-xl text-[#45423C] mb-3">{title}</h2>
      <table className="w-full text-sm">
        <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8577] text-left"><th className="pb-2">Όνομα</th><th className="pb-2 text-right">Εργασίες</th><th className="pb-2 text-right">Μ.Ο. χρόνου</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (<tr key={i} className="border-t border-[#F1EDE6]"><td className="py-2 text-[#45423C]">{r[0]}</td><td className="py-2 text-right tabular">{r[1]}</td><td className="py-2 text-right tabular text-[#6F6A60]">{r[2]}</td></tr>))}
          {rows.length === 0 && <tr><td colSpan={3} className="py-4 text-center text-[#8A8577]">Χωρίς δεδομένα</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
